import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import PlatformIcon from '../common/PlatformIcon';
import { ORANGE_ACTION } from '../../constants/colors';

export interface TimelineDayHeaderProps {
  day: number;
  date?: string;
}

export const TimelineDayHeader: React.FC<TimelineDayHeaderProps> = ({ day, date }) => {
  return (
    <View style={styles.dayHeaderWrapper}>
      <Text style={styles.dayHeaderText}>
        {day && day > 1 ? `第${day}天${date ? ` - ${date}` : ''}` : (date || '')}
      </Text>
      <View style={styles.dayHeaderDivider} />
    </View>
  );
};

export interface TimelineItemRowProps {
  time?: string; // left pill text, e.g., "09:00"
  children: React.ReactNode; // right-side content (usually a typed card)
  metaTagLabel?: string; // optional second-line meta label under the card (e.g., "🏷️ poi")
  showConnectorBelow?: boolean; // show a connector below the pill to connect to next item
  connectorProgress?: number;   // 0~1 之间的进度：0=未开始(全灰)，1=已完成(全橙)，中间为部分填充
  // Optional transport node icon (ultra minimal)
  showNode?: boolean;
  nodeIconName?: string;
  nodeColor?: string;
  nodeSize?: number;
  // Optional: handle press on the time pill (for inline edit)
  onPressTime?: () => void;
}

const ORANGE = ORANGE_ACTION;

export const TimelineItemRow: React.FC<TimelineItemRowProps> = ({ time = '09:00', children, metaTagLabel, showConnectorBelow = false, connectorProgress, showNode = false, nodeIconName, nodeColor = ORANGE, nodeSize = 12, onPressTime }) => {
  return (
    <View style={styles.timelineRow}>
      <View style={styles.timeColumn}>
        {onPressTime ? (
          <TouchableOpacity onPress={onPressTime} activeOpacity={0.8}>
            <View style={styles.timePill}><Text style={styles.timePillText}>{time}</Text></View>
          </TouchableOpacity>
        ) : (
          <View style={styles.timePill}><Text style={styles.timePillText}>{time}</Text></View>
        )}
        {showNode ? (
          <View style={styles.nodeWrapper}>
            {nodeIconName ? (
              <PlatformIcon name={nodeIconName} size={nodeSize} color={nodeColor} family="MaterialIcons" />
            ) : (
              <View style={[styles.nodeDot, { backgroundColor: nodeColor }]} />
            )}
          </View>
        ) : null}
        {showConnectorBelow ? (
          <View style={styles.connectorTrack}>
            {/* completed (top) */}
            <View style={[styles.connectorCompleted, { flex: clamp01(connectorProgress ?? 0) }]} />
            {/* remaining (bottom) */}
            <View style={[styles.connectorRemaining, { flex: 1 - clamp01(connectorProgress ?? 0) }]} />
          </View>
        ) : null}
      </View>
      <View style={styles.itemColumn}>
        {children}
        {metaTagLabel ? <Text style={styles.metaTagText}>{metaTagLabel}</Text> : null}
      </View>
    </View>
  );
};

// Helpers
const clamp01 = (v: number) => (isNaN(v) ? 0 : v < 0 ? 0 : v > 1 ? 1 : v);

const styles = StyleSheet.create({
  dayHeaderWrapper: { marginTop: 8, marginBottom: 8, paddingHorizontal: 16 },
  dayHeaderText: { fontSize: 14, color: '#111827', fontWeight: '600', marginBottom: 6 },
  dayHeaderDivider: { height: 1, backgroundColor: '#E5E7EB', width: '100%' },

  timelineRow: { flexDirection: 'row', alignItems: 'stretch' },
  // add top padding so the time pill lines up with the top of the adjacent card/image
  timeColumn: { width: 60, alignItems: 'center', justifyContent: 'flex-start', alignSelf: 'stretch', paddingTop: 16, paddingBottom: 16 },
  timePill: { backgroundColor: ORANGE, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 16 },
  timePillText: { color: '#fff', fontWeight: '600', fontSize: 12 },
  nodeWrapper: { marginTop: 6, alignSelf: 'center' },
  nodeDot: { width: 8, height: 8, borderRadius: 4, backgroundColor: '#9CA3AF' },
  itemColumn: { flex: 1, paddingLeft: 12, paddingRight: 20, alignSelf: 'stretch', paddingBottom: 16 },
  metaTagText: { marginTop: 6, color: '#9CA3AF', fontSize: 12 },
  connectorTrack: { marginTop: 8, width: 3, flex: 1, minHeight: 36, alignSelf: 'center', flexDirection: 'column' },
  connectorCompleted: { width: 3, backgroundColor: ORANGE, borderRadius: 1.5 },
  connectorRemaining: { width: 3, backgroundColor: '#D1D5DB', borderRadius: 1.5 },
});

export default undefined as unknown as never; // enforce named exports only
